import { errorMessage, phoneSchema } from "@workspace/domain/src/utils";
import * as z from "zod";

// BASE
const AgencySchema = z.object({
  id: z.string(),
  name: z.string(),
  username: z.string(),
  website: z.string().nullable(),
  description: z.any().nullable(),
  logoUrl: z.string().nullable(),
  phoneNumbers: z.string().array(),

  // Relations
  ownerId: z.string(),

  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const AgencyPrivateKeySchema = z.object({
  encryptedPrivateKey: z.string(),
  iv: z.string(),
  salt: z.string(),
});

// ACTIONS
const BaseSchema = z.object({
  // General information
  name: z
    .string(errorMessage("Name is required", "Name is invalid"))
    .nonempty("Name is required")
    .max(255, "Name must be less than 255 characters"),
  username: z
    .string(errorMessage("Username is required", "Username is invalid"))
    .nonempty("Username is required")
    .length(5, "Username must be 5 characters long")
    .regex(
      /^[a-zA-Z0-9]+$/,
      errorMessage(
        "Username is required",
        "Username must contain only letters and numbers",
      ),
    )
    .toUpperCase(),
  website: z
    .union([
      z.literal(""),
      z
        .string(errorMessage("Website is required", "Website is invalid"))
        .regex(
          /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/,
          "Website is invalid",
        ),
    ])
    .optional(),
  description: z
    .string(errorMessage("Description is required", "Description is invalid"))
    .optional(),
  phoneNumbers: z
    .array(
      z.union([
        phoneSchema("Phone number is required", "Phone number is invalid"),
        z.literal(""),
      ]),
    )
    .transform((arr) => arr.filter((val) => val !== "")),
});

const CreateSchema = BaseSchema.extend({
  ...AgencyPrivateKeySchema.shape,
  publicKey: z.string(),
});

const UpdateSchema = BaseSchema.extend(AgencyPrivateKeySchema.partial().shape);

export const AdminAgency = {
  Agency: AgencySchema,
  AgencyPrivateKey: AgencyPrivateKeySchema,
  Create: CreateSchema,
  Update: UpdateSchema,
};
export type AdminAgency = {
  Agency: z.infer<typeof AgencySchema>;
  AgencyPrivateKey: z.infer<typeof AgencyPrivateKeySchema>;
  Create: z.infer<typeof CreateSchema>;
  Update: z.infer<typeof UpdateSchema>;
};
